/**
 * Table row for a tutor on the gamification leaderboard.
 */
import * as React from 'react';
import { cn } from '@/lib/utils';
import { LeaderboardRankBadge } from '@/components/gamification/LeaderboardRankBadge';
import { TutorLeaderboardAvatar } from '@/components/gamification/TutorLeaderboardAvatar';
import { leaderboardRowClassName } from '@/components/gamification/gamification-utils';
import type { LeaderboardEntry } from '@/types/gamification.types';

/** Props for {@link TutorLeaderboardRow}. */
export interface TutorLeaderboardRowProps {
  entry: LeaderboardEntry;
  isHighlighted?: boolean;
  highlightLabel?: string;
  showRank?: boolean;
  compact?: boolean;
  className?: string;
}

/** Tutor leaderboard row: rank, avatar, name, and XP. */
export const TutorLeaderboardRow: React.FC<TutorLeaderboardRowProps> = ({
  entry,
  isHighlighted = false,
  highlightLabel = 'Vous',
  showRank = true,
  compact = false,
  className,
}) => {
  return (
    <div className={cn(leaderboardRowClassName(compact, isHighlighted), className)}>
      {showRank ? <LeaderboardRankBadge rank={entry.rank} /> : null}
      <TutorLeaderboardAvatar
        photoUrl={entry.photoUrl}
        displayName={entry.displayName}
        size={compact ? 'sm' : 'md'}
      />
      <div className="min-w-0 flex-1">
        <p className="truncate font-semibold text-foreground">
          {entry.displayName}
          {isHighlighted ? (
            <span className="ml-1.5 text-xs font-medium text-[var(--color-2-nuance-1)]">
              ({highlightLabel})
            </span>
          ) : null}
        </p>
      </div>
      <span className="shrink-0 text-sm font-bold text-[var(--color-1-nuance-1)]">
        {entry.totalXp} XP
      </span>
    </div>
  );
};
